import { PubSub } from '@aws-amplify/pubsub';
import MaterialTable from '@material-table/core';
import { AddIcCallOutlined, ClearOutlined, DarkModeOutlined, LightModeOutlined, PersonOutline, PhoneOutlined, RestoreOutlined } from "@mui/icons-material";
import {
	Box,
	Button,
	Card,
	CardContent,
	CardHeader,
	CardMedia,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	IconButton,
	InputLabel,
	List,
	ListItem,
	MenuItem,
	Select,
	Tab,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	Tabs,
	TextField,
	Tooltip,
	Typography
} from "@mui/material";
import { generateClient, post } from "aws-amplify/api";
import { fetchAuthSession } from "aws-amplify/auth";
import { enqueueSnackbar } from 'notistack';
import phone from 'phone';
import { useContext, useEffect, useRef, useState } from "react";
import { ActivityLog } from "../../components/contacts/activityLog";
import { CallStatusPane } from "../../components/engagement/callStatusPane";
import { UserContext } from "../../contexts/UserContext";
import { expandedContactSearch, getSegment, manualProfileByTenant } from "../../graphql/queries";
import { actionTwoButtonStyle, cardStyle, tabStyle } from "../../theme";
import { REGION } from '../../models/globals';


export function ManualDialing(props) {
	const client = generateClient();

	/** The user context state */
	const userContext = useContext(UserContext);

	/** Whether something is loading */
	const [loading, setLoading] = useState(true);

	/** Profiles  agent can manual dial from */
	const [manualProfiles, setManualProfiles] = useState([]);

	/** Which Profile Agent is dialing for */
	const [manualProfile, setManualProfile] = useState('');

	const [segment, setSegment] = useState();

	/** The full active contact data being manual dialed */
	const [activeContact, setActiveContact] = useState('');

	const [callingContact, setCallingContact] = useState(false);

	const [callStatus, setCallStatus] = useState({});

	const [phoneFields, setPhoneFields] = useState([]);

	const [currentTab, setCurrentTab] = useState(0);

	const [contactData, setContactData] = useState([]);

	const [openManualNumberDialog, setOpenManualNumberDialog] = useState(false);
	const [manualNumber, setManualNumber] = useState('');


	const pubsubRef = useRef(
    new PubSub({
      region: REGION,
      endpoint: `wss://a3b8mw0zsvy66t-ats.iot.${REGION}.amazonaws.com/mqtt`
    }),
  );


	const callStatusSubscription = useRef(null);

	useEffect(() => {
		if (userContext.apiKey) {
			getManualProfiles();
		}
		const tempPhoneFields = ['phone', 'cell', ...userContext.customFields.filter(field => field.type === 'Phone').map(field => field.name)];
		setPhoneFields(tempPhoneFields);
	}, [userContext.apiKey]);

	useEffect(() => {
		setLoading(false);
	}, [manualProfiles]);

	useEffect(() => {
		if (manualProfile) {
			subscribeToCallStatus();
			getSegmentDetails();
		}
	}, [manualProfile]);

	useEffect(() => {
		if (segment) {
			getContactsData();
		}
	}, [segment]);

	async function subscribeToCallStatus() {
		try {
			callStatusSubscription.current?.unsubscribe();
			const topic = `approvals/${process.env.REACT_APP_ENV}/${userContext.tenantId}/agent-call-statuses/${props.agentId}`;
			console.log('Subscribing to call status topic:', topic);
			callStatusSubscription.current =
				pubsubRef.current.subscribe({
					topics: topic,
				}).subscribe({
					next: (message) => {
						handleCallStatusChangeMessage(message);
					},
					error: (error) => {
						console.error('Error subscribing to call status', error);
					},
					complete: () => {
						console.log('Call Status subscription complete');
					}
				});

		} catch (error) {
			console.error('Error subscribing to call status', error);
		}
	}

	async function getSegmentDetails() {
		if (!manualProfile) {
			return;
		}
		try {
			const segmentResponse = await client.graphql({
				query: getSegment,
				variables: {
					id: manualProfile.segment,
				},
			});
			const segmentData = segmentResponse.data.getSegment;
			setSegment(segmentData);
		} catch (error) {
			console.error('Error fetching segment', error);
		}
	}

	function dayOfTheYear(date) {
		return Math.floor((date - new Date(date.getFullYear(), 0, 0)) / 1000 / 60 / 60 / 24);
	}

	async function getContactsData() {
		try {

			const fieldReplacements = ['#DOY']


			let options = {
				limit: 250,
			};
			let nextToken = null;
			options.filter = segment.query.searchFilter;
			// Handle Field Replacements
			for (const field of fieldReplacements) {
				switch (field) {
					case '#DOY':
						options.filter = options.filter.replaceAll(field, dayOfTheYear(new Date()));
						break;
					default:
						break;
				}
			}

			if (segment.query.sort.field !== '') {
				options.sort = segment.query.sort;
			}

			const contacts = [];

			do {
				if (nextToken) {
					options.nextToken = nextToken;
				}

				const listData = await client.graphql({
					query: expandedContactSearch,
					variables: options
				});

				const returnedContacts = listData.data.expandedContactSearch?.items ?? [];
				contacts.push(...returnedContacts);
				nextToken = listData.data.expandedContactSearch?.nextToken ?? null;
			} while (nextToken !== null);

			setContactData(contacts);
		}
		catch (error) {
			console.error(error);
		}
	}

	/**
 * Updates the call status on the approved contact when the status changes.
 *
 * @param {*} message The message received from the WebSocket.
 */
	async function handleCallStatusChangeMessage(message) {
		console.log('Call status change message received', message);
		setCallStatus({ callStatus: message.callStatus });
		return;

	}

	async function getManualProfiles() {
		try {
			setLoading(true);
			const idToken = (await fetchAuthSession()).tokens.idToken;;

			const profilesResponse = await client.graphql({
				query: manualProfileByTenant,
				variables: {
					tenant: idToken.payload['custom:tenant'],
					limit: 1000
				},
			});

			const returnedProfiles = profilesResponse.data.manualProfileByTenant?.items ?? [];

			// Filter profiles to manualDialing on the profile and the agent has the required skills
			let filteredProfiles = [];
			for (let profile of returnedProfiles) {
				// DEBUG Profile's coming
				// console.log(profile);
				// Check if profile skill (named skills) is included in agent's skills
				if (props.agentSkillIds.includes(profile.skills)) {
					filteredProfiles.push(profile);
				}
			}
			if (filteredProfiles.length === 0) {
				enqueueSnackbar('No manual dialing profiles available for your skills', { variant: 'warning' });
			}
			if (filteredProfiles.length === 1) {
				setManualProfile(filteredProfiles[0]);
			}
			setManualProfiles(filteredProfiles);

		} catch (error) {
			console.error(error);
		}
	}

	async function closeManual() {
		callStatusSubscription.current?.unsubscribe();
		setManualProfile('');
		setCallingContact(false);
		setCallStatus({});
		props.setDialMode('');
	}

	function getPhoneLabel(field) {
		switch (field) {
			case 'phone':
				return 'Phone';
			case 'cell':
				return 'Cell';
			default:
				let label = field
				userContext.customFields.find((customField) => {
					if (customField.name === field) {
						label = customField.displayName;
					}
				});
				return label;
		}
	}

	async function callContact(phone) {
		try {
			setCallingContact(true);

			enqueueSnackbar('Checking contact is valid for dialing', {
				variant: 'info',
				autoHideDuration: 2000,
			})


			const checkResult = await post({
				apiName: 'cdyxoutreach',
				path: '/dial/check',
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						contact: { ...activeContact, phone },
						field: 'phone',
						profileId: manualProfile.dialingProfile,
						tenantId: userContext.tenantId,
						suppressionProfileId: manualProfile.suppressionProfile || null
					}
				}
			}).response;

			const checkResponse = await checkResult.body.json();
			if (checkResponse?.Message !== 'Contact passed check') {
				enqueueSnackbar(checkResponse?.ErrorMessage || 'Error checking contact', {
					variant: 'error',
					autoHideDuration: 3000,
				});
				setCallingContact(false);
				return;
			} else {
				enqueueSnackbar(checkResponse.Message + '. Calling...', {
					variant: 'success',
					autoHideDuration: 2000,
				});
			}


			const dialResult = await post({
				apiName: 'cdyxoutreach',
				path: '/dial',
				options: {
					headers: {
						Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
						'x-api-key': userContext.apiKey,
					},
					body: {
						contact: { ...activeContact, phone },
						field: 'phone',
						dialingProfileId: manualProfile.dialingProfile,
						skillId: manualProfile.skills,
						agentId: props.agentId,
					}
				}
			}).response;

			const dialResponse = await dialResult.body.json();
			if (!dialResponse.contactId) {
				enqueueSnackbar('Error dialing contact', {
					variant: 'error',
					autoHideDuration: 3000,
				});
				setCallingContact(false);
				return;
			}

		} catch (error) {
			console.error('Error calling contact', error);
			setCallingContact(false);
		}
	}

	async function checkContact(phone) {
		const checkResult = await post({
			apiName: 'cdyxoutreach',
			path: '/dial/check',
			options: {
				headers: {
					Authorization: `Bearer ${(await fetchAuthSession()).tokens.idToken}`,
					'x-api-key': userContext.apiKey,
				},
				body: {
					contact: { ...activeContact, phone },
					field: 'phone',
					profileId: manualProfile.dialingProfile,
					tenantId: userContext.tenantId,
					suppressionProfileId: manualProfile.suppressionProfile || null
				}
			}
		}).response;

		const checkResponse = await checkResult.body.json();
		return checkResponse?.Message ? checkResponse.Message : checkResponse.errorMessage || 'Error checking contact';
	}


	return (
		<>

			{/* Manual Dialing */}
			<Box sx={{ width: '100%', height: '100vh' }} display="flex" flexDirection="column" alignItems="center" gap={2}>
				<Box sx={{ maxWidth: '1200px', width: '100%' }} display="flex" flexDirection="column" alignItems="center" gap={2}>

					{/* Campaign Control */}

					<Box sx={{ width: '100%', padding: '10px' }} display="grid" gridTemplateColumns="5fr 1fr" gap={2} alignItems="center">
						<FormControl>
							<InputLabel>Manual Dialing Profile</InputLabel>
							<Select
								label="Manual Dialing Profile"
								value={manualProfile}
								onChange={(e) => setManualProfile(e.target.value)}
							>
								{manualProfiles.map((profile) => (
									<MenuItem key={profile.id} value={profile}>{profile.name}</MenuItem>
								))}
							</Select>
						</FormControl>
						{!callingContact && <Button onClick={closeManual} sx={actionTwoButtonStyle}>Close Manual Dial</Button>}
					</Box>

					{/* Contact Table filtered with the segment */}
					{!activeContact && !callingContact && manualProfile && segment &&
						<Box sx={{ width: '100%', padding: '10px' }} display="flex" flexDirection="column" alignItems="center">
							<Card variant='outlined' sx={cardStyle}>
								<CardContent>
									<MaterialTable
										components={{
											// Toolbar: toolBar,
											Container: (props) => <Box {...props} elevation={0} />,
										}}
										title={`Manual Dial Contacts for ${manualProfile.name}`}
										data={contactData}
										detailPanel={({ rowData }) => {
											const customFields = JSON.parse(rowData.customFields);
											return (
												<Card variant="outlined" sx={cardStyle}>
													<CardHeader title="Custom Fields" slotProps={{
														title: {
															variant: "body2",
															color: "textSecondary",
														}
													}} />
													<CardContent>
														<Table size="small">
															<TableHead>
																<TableRow>
																	{userContext.customFields?.map((field) => {
																		if (!customFields[field.name]) {
																			return null;
																		} else {
																			return (
																				<TableCell variant='h6' key={field.name}>{field.name}</TableCell>
																			);
																		}
																	})}
																</TableRow>
															</TableHead>
															<TableBody>
																<TableRow>
																	{userContext.customFields?.map((field) => {
																		if (!customFields[field.name]) {
																			return null;
																		} else if (field.type === 'Boolean') {
																			return (
																				<TableCell key={field.name}>
																					{customFields[field.name] ? <CheckCircleOutline color="primary" /> : 'False'}
																				</TableCell>
																			);
																		}
																		return (
																			<TableCell key={field.name}>
																				{customFields[field.name]}
																			</TableCell>
																		);
																	})}
																</TableRow>
															</TableBody>
														</Table>
													</CardContent>
												</Card>
											);
										}}
										columns={[
											{
												title: 'External ID', field: 'externalId', width: '20%', render: (rowData) => (
													<div
														style={{
															maxHeight: "26px",
															overflow: "hidden",
														}}
													>
														{rowData.externalId}
													</div>)
											},
											{ title: 'First Name', field: 'firstName' },
											{ title: 'Last Name', field: 'lastName' },
											{ title: 'Source', field: 'source' },
											{ title: 'Phone', field: 'phone' },
											{ title: 'Cell', field: 'cell' },
											{ title: 'State', field: 'state' },
											{ title: 'Zip Code', field: 'zip' },
										]}
										isLoading={loading}
										options={{
											maxBodyHeight: 'calc(100vh - 350px)',
											actionsColumnIndex: -1,
											pageSize: 10,
											searchAutoFocus: true,
											searchFieldStyle: {
												marginBottom: '16px',
												marginleft: '-28px',
											},
											headerStyle: {
												borderTop: '1px solid #e0e0e0',
												padding: '16px',
											},
											searchFieldVariant: 'outlined',
											paginationType: 'stepped',
										}}
										actions={[
											{
												icon: () => <PhoneOutlined color="primary" />,
												onClick: (event, rowData) => {
													rowData.customFields = JSON.parse(rowData.customFields);
													delete rowData?.tableData;
													setActiveContact(rowData);
												}
											}
										]}
									/>
								</CardContent>
							</Card>
						</Box>
					}

					{/* Contact Control */}

					{activeContact && <>
						<Box sx={{ width: '100%', padding: '10px' }} display="flex" flexDirection="column" alignItems="center">
							<Card sx={{ width: '100%', borderRadius: '7px' }} variant="outlined">
								<CardMedia sx={{ height: '200px', backgroundColor: '#b9cde8' }}>
									<Box height="100%" >
										{callingContact &&
											<CallStatusPane
												contact={callStatus}
												onDismiss={() => {
													setCallingContact(false);
													setCallStatus({});
												}}
											/>
										}
									</Box>
								</CardMedia>
								<Box sx={{ padding: '10px' }} display="grid" gridTemplateColumns="1fr 1fr 1fr">
									<Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
										{/* <List> */}
										{/* Display the phone field if it exists on the contact */}
										{phoneFields.map((field) => {
											if (activeContact && (activeContact[field] || activeContact.customFields?.[field])) {
												return (
													<ListItem key={field} secondaryAction={!callingContact &&
														// <Tooltip title={`Call ${getPhoneLabel(field)}`} >
														<IconButton
															sx={[
																{ backgroundColor: '#1559b3' },
																{ '&:hover': { backgroundColor: '#0c3e9a' } },
															]}
															edge="end" onClick={() => callContact(activeContact[field] || activeContact.customFields[field])}>
															<PhoneOutlined fontSize="small" color="white" />
														</IconButton>
														// </Tooltip>
													}>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact[field] ? activeContact[field] : activeContact.customFields[field]}</Typography>
															<Typography variant="body2">{getPhoneLabel(field)}</Typography>
														</Box>
													</ListItem>
												);
											}
										})}
										{/* </List> */}
									</Box>
									<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
										<Typography variant="h5">{activeContact.firstName} {activeContact.lastName}</Typography>
										{activeContact.externalId && <Typography variant="h6">{`External ID: ${activeContact.externalId}`}</Typography>}
									</Box>
									<Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
										{activeContact && !callingContact && <Box display="flex" flexDirection="row" gap={2} justifyContent="center">
											<Tooltip title="Dial a manual number for this contact">
												<IconButton
													sx={[
														{ backgroundColor: '#1559b3' },
														{ '&:hover': { backgroundColor: '#0c3e9a' } },
													]}
													onClick={() => setOpenManualNumberDialog(true)}
												>
													<AddIcCallOutlined fontSize="small" color="white" />
												</IconButton>
											</Tooltip>
											<Tooltip title="Dismiss contact and return to contact list">
												<IconButton
													sx={[
														{ backgroundColor: '#f44336' },
														{ '&:hover': { backgroundColor: '#d32f2f', } }
													]}
													onClick={() => setActiveContact('')}>
													<ClearOutlined fontSize="small" color="white" />
												</IconButton>
											</Tooltip>
										</Box>}
									</Box>
								</Box>
								<Box sx={{ backgroundColor: '#f2f6fa', borderBottomLeftRadius: '7px', borderBottomRightRadius: '7px' }} display="flex" flexDirection="row" justifyContent="flex-end">
									{activeContact && <Tabs
										value={currentTab}
										onChange={(e, newValue) => setCurrentTab(newValue)}
										indicatorColor="primary"
										textColor="primary"
									>
										<Tab iconPosition="start" icon={<PersonOutline />} sx={tabStyle} label='Contact Info'></Tab>
										<Tab iconPosition="start" icon={<RestoreOutlined />} sx={tabStyle} label='Activity Log'></Tab>
									</Tabs>}
								</Box>
							</Card>
						</Box >

						{/* Contact Info */}
						<Box sx={{ width: '100%', padding: '10px' }} display="flex" flexDirection="column" alignItems="center">
							<Card elevation={0} sx={{ width: '100%' }}>
								<Box>
									{currentTab === 0 && <Box display="grid" gridTemplateColumns="1fr 2fr" gap={2}>
										<Card variant="outlined" sx={cardStyle}>
											<CardContent>
												<Typography variant="h5">Contact Info</Typography>
												<List>
													{activeContact?.source && <ListItem>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact.source ? activeContact.source : 'No Source'}</Typography>
															<Typography variant="body2">Source</Typography>
														</Box>
													</ListItem>}
													{activeContact?.email && <ListItem>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact.email ? activeContact.email : 'No Email'}</Typography>
															<Typography variant="body2">Email</Typography>
														</Box>
													</ListItem>}
													{activeContact?.address1 && <ListItem>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact.address1 ? activeContact.address1 : 'No Address 1'}</Typography>
															<Typography variant="body2">Address 1</Typography>
														</Box>
													</ListItem>}
													{activeContact?.address2 && <ListItem>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact.address2 ? activeContact.address2 : 'No Address 2'}</Typography>
															<Typography variant="body2">Address 2</Typography>
														</Box>
													</ListItem>}
													{activeContact?.city && <ListItem>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact.city ? activeContact.city : 'No City'}</Typography>
															<Typography variant="body2">City</Typography>
														</Box>
													</ListItem>}
													{activeContact?.state && <ListItem>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact.state ? activeContact.state : 'No State'}</Typography>
															<Typography variant="body2">State</Typography>
														</Box>
													</ListItem>}
													{activeContact?.zip && <ListItem>
														<Box display="flex" flexDirection="column">
															<Typography variant="body1">{activeContact.zip ? activeContact.zip : 'No Zip'}</Typography>
															<Typography variant="body2">Zip</Typography>
														</Box>
													</ListItem>}
													{!activeContact.email && !activeContact.address1 && !activeContact.address2 && !activeContact.city && !activeContact.state && !activeContact.zip &&
														<Typography sx={{ fontStyle: 'italic' }} variant="body1">No Additional Contact Info</Typography>}
												</List>
											</CardContent>
										</Card>
										<Card variant="outlined" sx={cardStyle}>
											<CardContent>
												<Typography variant="h5">Custom Fields</Typography>
												<Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
													{userContext.customFields.map((field) => {
														if (activeContact.customFields[field.name] && field.type !== 'Phone') {
															return (
																<ListItem key={field.name}>
																	<Box display="flex" flexDirection="column">
																		<Typography variant="body1">{activeContact.customFields[field.name]}</Typography>
																		<Typography variant="body2">{field.displayName}</Typography>
																	</Box>
																</ListItem>
															);
														}
													})}
												</Box>
											</CardContent>
										</Card>

									</Box>}
									{currentTab === 1 &&
										<Card variant="outlined" sx={cardStyle}>
											<Box sx={{ padding: '10px' }}>
												<ActivityLog contactId={activeContact.id} />
											</Box>
										</Card>}
								</Box>
							</Card>
						</Box>
					</>}
				</Box >
			</Box >
			<Dialog open={openManualNumberDialog} onClose={() => setOpenManualNumberDialog(false)}>
				<DialogTitle>Dial A Manual Number</DialogTitle>
				<DialogContent>
					<Box display="flex" gap={2} alignItems="center" sx={{ marginTop: '10px' }}>
						<TextField
							label="Phone Number"
							value={manualNumber}
							onChange={(e) => setManualNumber(phone(e.target.value).phoneNumber)}
							fullWidth
							variant="outlined"
							sx={{ marginBottom: '10px' }}
						/>
						<IconButton
							disabled={!phone(manualNumber).isValid}
							onClick={() => {
								if (manualNumber) {
									callContact(manualNumber)
									setManualNumber('');
									setOpenManualNumberDialog(false);
								}
							}}
							sx={[{ backgroundColor: '#1559b3' }, {
								'&:hover': {
									backgroundColor:
										'#0c3e9a'
								}
							}]}
						>
							<PhoneOutlined fontSize="small" color="white" />
						</IconButton>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button sx={actionTwoButtonStyle} onClick={() => {
						setOpenManualNumberDialog(false)
						setManualNumber('');
					}}>
						Cancel
					</Button>
				</DialogActions>
			</Dialog >
		</>
	)
}
